import React from 'react';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { adImageUrl } from '../../services/AdCreativeViewer';

/**
 * Ad screenshots for a paid traffic find.
 *
 * Used by both channels. A Google sponsored result disappears from the SERP as
 * soon as the campaign stops, exactly like a Meta ad leaves the Ad Library, so
 * the screenshot is the only durable evidence of either — there is no reason
 * this belonged to Meta finds alone.
 *
 * Two modes, because images attach to a log entry: while a find is being
 * created there is no entry yet, so files are held and uploaded on save. When
 * an existing find is edited they upload immediately.
 */
const AdScreenshotsField = ({
  channel,
  isEditingEntry,
  existingImages,
  pendingImages,
  uploadingImages,
  onUpload,
  onRemoveExisting,
  onAddPending,
  onRemovePending
}) => {
  const isSocial = channel !== 'google';

  return (
    <div>
      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">
        {isSocial ? 'Ad screenshots' : 'Sponsored result screenshots'}
      </label>

      {isEditingEntry ? (
        <div className="space-y-2">
          {existingImages.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {existingImages.map((imagePath) => (
                <div key={imagePath} className="group relative">
                  <img
                    src={adImageUrl(imagePath)}
                    alt="Ad creative"
                    className="h-16 w-16 rounded-lg border border-gray-200 object-cover dark:border-slate-700"
                  />
                  <button
                    type="button"
                    onClick={() => onRemoveExisting(imagePath)}
                    title="Remove this screenshot"
                    className="absolute -right-1.5 -top-1.5 rounded-full bg-red-600 p-0.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-600 hover:border-blue-400 hover:text-blue-600 dark:border-slate-600 dark:text-slate-300">
            {uploadingImages ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ImagePlus className="h-3.5 w-3.5" />
            )}
            {uploadingImages ? 'Uploading…' : 'Add screenshots'}
            <input
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              multiple
              className="hidden"
              disabled={uploadingImages}
              onChange={(e) => onUpload(e.target.files, e.target)}
            />
          </label>
        </div>
      ) : (
        <div className="space-y-2">
          {pendingImages.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {pendingImages.map((item, index) => (
                <div key={item.previewUrl} className="group relative">
                  <img
                    src={item.previewUrl}
                    alt={`Ad creative ${index + 1}`}
                    className="h-16 w-16 rounded-lg border border-gray-200 object-cover dark:border-slate-700"
                  />
                  <button
                    type="button"
                    onClick={() => onRemovePending(index)}
                    title="Remove"
                    className="absolute -right-1.5 -top-1.5 rounded-full bg-red-600 p-0.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-600 hover:border-blue-400 hover:text-blue-600 dark:border-slate-600 dark:text-slate-300">
            <ImagePlus className="h-3.5 w-3.5" />
            Add screenshots
            <input
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              multiple
              className="hidden"
              onChange={(e) => onAddPending(e.target.files, e.target)}
            />
          </label>
          <p className="text-xs text-gray-400 dark:text-slate-500">
            {pendingImages.length > 0
              ? `${pendingImages.length} image${pendingImages.length === 1 ? '' : 's'} will upload when you save this find.`
              : isSocial
                ? 'Attach the ad creative — it stays viewable after the campaign ends.'
                : 'Attach the sponsored result — it stays viewable after the campaign ends.'}
          </p>
        </div>
      )}

      <p className="mt-2 text-xs text-gray-400 dark:text-slate-500">
        {isSocial
          ? 'A snapshot of the Ad Library card as it reads today. It is never refreshed, so it records what you saw rather than what is running now.'
          : 'A snapshot of the sponsored result as it appeared in the SERP. It is never refreshed, so it records what you saw rather than what is running now.'}
      </p>
    </div>
  );
};

export default AdScreenshotsField;
